import { BrowserModule } from '@angular/platform-browser';
import { APP_INITIALIZER, ErrorHandler, NgModule, Provider } from '@angular/core';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { HttpClientModule } from '@angular/common/http';
import { Router } from '@angular/router';

import { MAT_CHECKBOX_DEFAULT_OPTIONS } from '@angular/material/checkbox';
import { MAT_DIALOG_DEFAULT_OPTIONS, MatDialogModule } from '@angular/material/dialog';
import { MAT_FORM_FIELD_DEFAULT_OPTIONS } from '@angular/material/form-field';
import { MAT_RADIO_DEFAULT_OPTIONS } from '@angular/material/radio';
import { MAT_SNACK_BAR_DEFAULT_OPTIONS, MatSnackBarModule } from '@angular/material/snack-bar';

import { AppComponent } from './app.component';

import { AppNotificationService } from './services/app/notification.service';

import { AlertService } from './services/core/alert.service';
import { ApiKeyService } from './services/core/api-key.service';
import { AuthService } from './services/core/auth.service';
import { AupService } from './services/core/aup.service';
import { BusinessService } from './services/core/business.service';
import { CalendarService } from './services/core/calendar.service';
import { JWTService } from './services/core/jwt.service';
import { InvoiceService } from './services/core/invoice.service';
import { LicenseService } from './services/core/license.service';
import { LocationService } from './services/core/location.service';
import { LutService } from './services/core/lut.service';
import { MemberService } from './services/core/member.service';
import { PaymentMethodService } from './services/core/payment-method.service';
import { RestService } from './services/core/rest.service';
import { TenantService } from './services/core/tenant.service';
import { UserService } from './services/core/user.service';

import { StaffBusinessService } from './services/staff/business.service';
import { StaffCalendarService } from './services/staff/calendar.service';
import { StaffLicenseService } from './services/staff/license.service';
import { StaffLocationService } from './services/staff/location.service';
import { StaffInvoiceService } from './services/staff/invoice.service';
import { StaffMemberService } from './services/staff/member.service';
import { StaffOrderCommentService } from './services/staff/order-comment.service';
import { StaffOrderService } from './services/staff/order.service';
import { StaffReportService } from './services/staff/report.service';
import { StaffTaskService } from './services/staff/task.service';
import { StaffTenantService } from './services/staff/tenant.service';
import { StaffUserService } from './services/staff/user.service';

import { AppPreferenceService } from './services/app/preference.service';
import { AppRoutingModule } from './app-routing.module';
import { AppWorkflowService } from './services/app/workflow.service';

import { environment } from '../environments/environment';

import * as Sentry from '@sentry/angular';

const sentryProviders: Provider[] = environment.production
  ? [
      {
        provide: ErrorHandler,
        useValue: Sentry.createErrorHandler(),
      },
      {
        provide: Sentry.TraceService,
        deps: [Router],
      },
      {
        provide: APP_INITIALIZER,
        useFactory: () => () => {},
        deps: [Sentry.TraceService],
        multi: true,
      },
    ]
  : [];

@NgModule({
  declarations: [AppComponent],
  imports: [
    BrowserModule,
    BrowserAnimationsModule,
    HttpClientModule,
    MatDialogModule,
    MatSnackBarModule,
    AppRoutingModule,
  ],
  providers: [
    ...sentryProviders,

    { provide: MAT_SNACK_BAR_DEFAULT_OPTIONS, useValue: { duration: 5000 } },
    {
      provide: MAT_DIALOG_DEFAULT_OPTIONS,
      useValue: {
        hasBackdrop: true,
        width: '350px',
      },
    },
    {
      provide: MAT_CHECKBOX_DEFAULT_OPTIONS,
      useValue: {
        color: 'primary',
      },
    },
    {
      provide: MAT_RADIO_DEFAULT_OPTIONS,
      useValue: {
        color: 'primary',
      },
    },
    {
      provide: MAT_FORM_FIELD_DEFAULT_OPTIONS,
      useValue: {
        appearance: 'fill',
      },
    },

    AppNotificationService,
    AppPreferenceService,
    AppWorkflowService,

    AlertService,
    ApiKeyService,
    AuthService,
    AupService,
    BusinessService,
    CalendarService,
    JWTService,
    InvoiceService,
    LicenseService,
    LocationService,
    LutService,
    MemberService,
    PaymentMethodService,
    RestService,
    TenantService,
    UserService,

    StaffBusinessService,
    StaffCalendarService,
    StaffInvoiceService,
    StaffLicenseService,
    StaffLocationService,
    StaffMemberService,
    StaffOrderCommentService,
    StaffOrderService,
    StaffReportService,
    StaffTaskService,
    StaffTenantService,
    StaffUserService,
  ],
  bootstrap: [AppComponent],
})
export class AppModule {}
